/**
 * Column definitions for the permissions matrix.
 *
 * Split from the page because they are a pure function of their handlers: no
 * state, no effects, and two hundred lines of layout that the page's own logic
 * had to be read around.
 */

import type { ColumnDef } from '@tanstack/react-table'
import {
  Check,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  X,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { ColorBadge } from '@/components/ColorBadge'
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { cn } from '@/lib/utils'
import { SECTION_ORDER } from '../capabilities'
import { isEditable, rolesLockedByPhi } from '../matrix'
import type { MatrixRow, RoleKey } from '../types'
import { OrgScopeSelect } from './OrgScopeSelect'
import { RoleCheckboxGroup } from './RoleCheckboxGroup'

/**
 * The reference columns are pinned to just fit their longest value so Permission
 * keeps as much width as possible — it holds five labelled checkboxes and wraps
 * badly when squeezed. Capability wraps rather than widens; Page cannot, since
 * its contents sit on one line.
 *
 * The table lays out `fixed` from TanStack's numeric `size`, so that is what
 * actually drives the width; the matching classes keep the cells from stretching.
 */
const COL_SIZE = {
  // Fits the "Agent tools" pill (83px at 12px/500 Inter) plus the cell's 16px
  // padding and 1px border, with headroom — sized to exactly 100 left the pill
  // overflowing by a hair on wider font rendering.
  section: 112,
  // Holds the chevron slot, the longest page name ("Chronic Conditions", 131px
  // at 14px Inter) and the "Nav TBD" pill beside it, all on one line.
  page: 240,
  route: 164,
  // Wide enough for the "Non-PHI" badge (67px + 17px of padding and border).
  phi: 96,
  // Header text only — the cells hold a tick or a dash, so "Write" at 14px plus
  // the cell's padding and border is the whole requirement.
  flag: 76,
  capability: 240,
  orgs: 148,
  permission: 460,
} as const

const COL = {
  section: 'w-[112px] min-w-[112px] max-w-[112px]',
  page: 'w-[240px] min-w-[240px] max-w-[240px]',
  route: 'w-[164px] min-w-[164px] max-w-[164px]',
  phi: 'w-[96px] min-w-[96px] max-w-[96px]',
  flag: 'w-[76px] min-w-[76px] max-w-[76px]',
  capability: 'w-[240px] min-w-[240px] max-w-[240px]',
  orgs: 'w-[148px] min-w-[148px] max-w-[148px]',
  permission: 'min-w-[460px]',
} as const

/**
 * Cell class for columns whose content is atomic rather than prose.
 *
 * A badge is a pill and a dropdown is a control; neither is text that can be
 * shortened, but the table's base cell class carries `truncate` — so when the pill's box overflows by even a pixel
 * of its own padding, the cell paints an ellipsis beside text that is already
 * fully readable. Clipping keeps the guard against spilling into the next
 * column without the misleading "…".
 *
 * Overflow and whitespace are spelled out because `text-clip` conflicts with
 * `truncate`: tailwind-merge drops the whole shorthand, taking `overflow-hidden`
 * and `whitespace-nowrap` with it, so they have to be restated here.
 */
const BADGE_CELL = 'overflow-hidden whitespace-nowrap text-clip'

function EmDash() {
  return <span className="text-muted-foreground">—</span>
}

/**
 * Read and Write on the row that IS a page.
 *
 * They answer what exercising a capability does, and a page is not something
 * exercised — the rows nested under it are. Drawing a tick or a cross here
 * puts the header in apparent disagreement with every child below it.
 */
function PageRowFlag() {
  return (
    <span
      aria-label="Not applicable to a page"
      title="The rows under this page say what each control does.">
      <EmDash />
    </span>
  )
}

/**
 * A `read` or `write` flag, either way round.
 *
 * Labelled rather than left as a bare glyph: the icon is the scannable half,
 * and the label is what a screen reader and the tooltip say. On a PHI row
 * `read` is the reason the roles without clearance are greyed out, so the
 * explanation belongs next to the flag that causes it.
 */
function Flag({
  set,
  label,
  title,
}: {
  set: boolean
  label: string
  title: string
}) {
  return (
    <span
      aria-label={label}
      title={title}
      className={cn(
        'inline-flex',
        set ? 'text-success-solid' : 'text-error-solid'
      )}>
      {set ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
    </span>
  )
}

export function buildColumns({
  onRolesChange,
  onPageRolesChange,
  onPageToggle,
  showOrgs,
  onOrgScopeChange,
  onPageOrgScopeChange,
  allOrgs,
}: {
  onRolesChange: (id: string, next: RoleKey[]) => void
  /**
   * A page row's boxes stand for every row on the page. Given the ids, the
   * roles they all held, and what the operator left ticked, apply the one
   * role that changed to all of them.
   */
  onPageRolesChange: (ids: string[], before: RoleKey[], next: RoleKey[]) => void
  /** Opens or closes one page's dependent rows. */
  onPageToggle: (pageKey: string) => void
  /** The org column belongs to the Careforce scope alone. */
  showOrgs: boolean
  onOrgScopeChange: (id: string, next: string[]) => void
  /** A page row's dropdown moves every row on the page together. */
  onPageOrgScopeChange: (ids: string[], next: string[]) => void
  allOrgs: string[]
}): ColumnDef<MatrixRow>[] {
  return [
    {
      id: 'section',
      accessorKey: 'section',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Section" />
      ),
      enableSorting: false,
      size: COL_SIZE.section,
      meta: {
        headerClassName: COL.section,
        cellClassName: `${COL.section} ${BADGE_CELL}`,
      },
      // Blank on a nested row: nesting groups by section and page, so a
      // dependent's are the parent's by construction, and repeating them down
      // the block is noise the eye has to re-read.
      cell: ({ row }) =>
        row.original.dependent ? null : (
          <ColorBadge
            value={row.original.section}
            // Index by section order so the seven sections get distinct colors
            // rather than risking a hash collision.
            colorIndex={SECTION_ORDER.indexOf(
              row.original.section as (typeof SECTION_ORDER)[number]
            )}
            className="text-xs"
          />
        ),
    },
    {
      accessorKey: 'page',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Page" />
      ),
      enableSorting: false,
      size: COL_SIZE.page,
      meta: {
        headerClassName: COL.page,
        cellClassName: `${COL.page} ${BADGE_CELL}`,
      },
      cell: ({ row }) =>
        row.original.dependent ? null : (
          <div className="flex items-start gap-1">
            {/* The slot is held whether or not there is a chevron, so the page
              names line up down the column. */}
            <span className="mt-0.5 flex w-3.5 shrink-0 justify-center">
              {row.original.childCount ? (
                <button
                  type="button"
                  aria-expanded={row.original.expanded ?? false}
                  aria-label={`${row.original.expanded ? 'Collapse' : 'Expand'} ${row.original.page}`}
                  onClick={() => onPageToggle(row.original.pageKey as string)}
                  className="rounded text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
                  {row.original.expanded ? (
                    <ChevronDown size={14} aria-hidden />
                  ) : (
                    <ChevronRight size={14} aria-hidden />
                  )}
                </button>
              ) : null}
            </span>
            <span className="text-sm">{row.original.page}</span>
            {row.original.pageHidden ? (
              <Badge
                variant="secondary"
                className="shrink-0 text-xs"
                title={
                  'No role can open this page, so it is missing from the sidebar ' +
                  'and its route is refused. Grant this capability to put it back.'
                }>
                Nav TBD
              </Badge>
            ) : null}
          </div>
        ),
    },
    {
      accessorKey: 'route',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Route" />
      ),
      enableSorting: false,
      size: COL_SIZE.route,
      meta: { headerClassName: COL.route, cellClassName: COL.route },
      cell: ({ row }) =>
        row.original.route ? (
          <code className="rounded bg-muted px-1 py-0.5 text-xs">
            {row.original.route}
          </code>
        ) : (
          <EmDash />
        ),
    },
    {
      id: 'phi',
      accessorFn: (row) => (row.phi ? 'phi' : 'non-phi'),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="PHI" />
      ),
      enableSorting: false,
      size: COL_SIZE.phi,
      meta: {
        headerClassName: COL.phi,
        cellClassName: `${COL.phi} ${BADGE_CELL}`,
      },
      cell: ({ row }) => (
        <Badge
          variant={row.original.phi ? 'destructive' : 'success'}
          className="text-xs">
          {row.original.phi ? 'PHI' : 'Non-PHI'}
        </Badge>
      ),
    },
    {
      id: 'read',
      accessorFn: (row) => (row.read ? 'read' : 'no-read'),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Read" />
      ),
      enableSorting: false,
      size: COL_SIZE.flag,
      meta: {
        headerClassName: COL.flag,
        cellClassName: `${COL.flag} ${BADGE_CELL}`,
      },
      cell: ({ row }) =>
        row.original.pageRow ? (
          <PageRowFlag />
        ) : (
          <Flag
            set={row.original.read}
            label={row.original.read ? 'Displays data' : 'Displays nothing'}
            title={
              row.original.read
                ? row.original.phi
                  ? 'Displays PHI, so the roles without clearance are locked.'
                  : 'Puts the underlying data in front of the holder.'
                : 'Renders nothing back to the holder.'
            }
          />
        ),
    },
    {
      id: 'write',
      accessorFn: (row) => (row.write ? 'write' : 'no-write'),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Write" />
      ),
      enableSorting: false,
      size: COL_SIZE.flag,
      meta: {
        headerClassName: COL.flag,
        cellClassName: `${COL.flag} ${BADGE_CELL}`,
      },
      cell: ({ row }) =>
        row.original.pageRow ? (
          <PageRowFlag />
        ) : (
          <Flag
            set={row.original.write}
            label={row.original.write ? 'Changes data' : 'Changes nothing'}
            title={
              row.original.write
                ? 'Creates, changes or removes data, or triggers a side effect.'
                : 'Leaves stored state untouched.'
            }
          />
        ),
    },
    {
      accessorKey: 'capability',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Capability" />
      ),
      enableSorting: false,
      size: COL_SIZE.capability,
      meta: {
        headerClassName: COL.capability,
        cellClassName: COL.capability,
      },
      cell: ({ row }) => (
        <span
          className={cn(
            'flex items-start gap-1 whitespace-normal text-sm',
            // Indented under the row that opens the page, so the relationship
            // reads off the column rather than having to be worked out from
            // the page name repeating.
            row.original.dependent && 'pl-5',
            row.original.pageHidden && 'text-muted-foreground'
          )}>
          {row.original.dependent ? (
            <span
              role="img"
              aria-label={`Reached from ${row.original.page}`}
              className="mt-0.5 shrink-0 text-muted-foreground">
              <CornerDownRight size={13} aria-hidden />
            </span>
          ) : null}
          <span className="min-w-0">
            {row.original.capability}
            {row.original.childCount ? (
              <span className="ml-1 text-xs text-muted-foreground">
                ({row.original.childCount}{' '}
                {row.original.childCount === 1 ? 'item' : 'items'})
              </span>
            ) : null}
          </span>
        </span>
      ),
    },
    ...(showOrgs
      ? [
          {
            id: 'orgs',
            header: ({ column }) => (
              <DataTableColumnHeader column={column} title="Orgs" />
            ),
            enableSorting: false,
            size: COL_SIZE.orgs,
            meta: {
              headerClassName: COL.orgs,
              // A control fills its cell, so the base `truncate` paints an
              // ellipsis beside something already fully readable.
              cellClassName: `${COL.orgs} ${BADGE_CELL}`,
            },
            cell: ({ row }) => (
              <OrgScopeSelect
                label={row.original.capability}
                // A page row shows the union of what its rows reach and sets
                // all of them, so a child can never exceed the header.
                value={row.original.pageOrgs ?? row.original.orgs ?? []}
                orgs={allOrgs}
                // Greyed for the same reason the roles are: nothing on the
                // server consults this row, so moving it between orgs would
                // look like a decision and take no effect.
                disabled={!isEditable(row.original)}
                onChange={(next) =>
                  row.original.pageIds
                    ? onPageOrgScopeChange(row.original.pageIds, next)
                    : onOrgScopeChange(row.original.id, next)
                }
              />
            ),
          } as ColumnDef<MatrixRow>,
        ]
      : []),
    {
      id: 'permission',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Permission" />
      ),
      enableSorting: false,
      enableHiding: false,
      size: COL_SIZE.permission,
      meta: {
        headerClassName: COL.permission,
        cellClassName: COL.permission,
      },
      cell: ({ row }) =>
        row.original.pageIds ? (
          <RoleCheckboxGroup
            label={row.original.capability}
            value={row.original.pageRolesAll ?? []}
            mixed={(row.original.pageRolesSome ?? []).filter(
              (role) => !(row.original.pageRolesAll ?? []).includes(role)
            )}
            disabled={!isEditable(row.original)}
            // Not the page row's own PHI flag, which says nothing about the
            // rows under it: locked only where every row on the page locks it.
            // Where some do, the change still reaches the others.
            locked={row.original.pageRolesLocked ?? []}
            onChange={(next) =>
              onPageRolesChange(
                row.original.pageIds as string[],
                row.original.pageRolesAll ?? [],
                next
              )
            }
          />
        ) : (
          <RoleCheckboxGroup
            label={row.original.capability}
            value={row.original.roles}
            disabled={!isEditable(row.original)}
            locked={rolesLockedByPhi(row.original.id)}
            onChange={(next) => onRolesChange(row.original.id, next)}
          />
        ),
    },
  ]
}
